"use client";

import React, { useState } from "react";
import { Cpu, Bot, Compass, Eye, Navigation, Trophy, Users, Clock, ArrowRight, Languages, CircleCheck as CheckCircle2, Sparkles, Layers, Wrench, Activity } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface CourseData {
  code: string;
  titleVi: string;
  titleEn: string;
  ageVi: string;
  ageEn: string;
  prereqVi: string;
  prereqEn: string;
  conceptVi: string;
  conceptEn: string;
  focusVi: string[];
  focusEn: string[];
  link: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const COURSES: CourseData[] = [{
  code: "HP1",
  titleVi: "Kỹ sư cơ khí nhí",
  titleEn: "Junior Mechanical Engineer",
  ageVi: "Từ 7 tuổi",
  ageEn: "Ages 7+",
  prereqVi: "D4 hoặc đạt bài kiểm tra đầu vào",
  prereqEn: "Completed D4 or entrance assessment",
  conceptVi: "Bắt đầu lập trình robot thực sự.",
  conceptEn: "Beginning genuine robot programming.",
  focusVi: ["Controller", "Motor", "Block Studio", "Lập trình tuần tự", "Tốc độ & Thời gian", "Góc quay", "Vòng lặp", "Bánh răng & Đòn bẩy", "Cơ cấu tay quay"],
  focusEn: ["Controller", "Motor", "Block Studio", "Sequential logic", "Speed & Time", "Angle control", "Loops", "Gears & Levers", "Crank mechanism"],
  link: "/RoboticsCourseHP1Page",
  icon: Wrench
}, {
  code: "HP2",
  titleVi: "Robot biết cảm nhận",
  titleEn: "Sensing & Responsive Robot",
  ageVi: "Từ 7–8 tuổi",
  ageEn: "Ages 7–8",
  prereqVi: "Hoàn thành HP1",
  prereqEn: "Prerequisite: HP1 completed",
  conceptVi: "Robot bắt đầu cảm nhận và phản hồi môi trường.",
  conceptEn: "Robots start sensing and responding to surroundings.",
  focusVi: ["Cảm biến hồng ngoại", "Cảm biến chạm", "Lệnh If / If-Else", "Wait Until", "Biến số & Bộ đếm", "Bánh răng nón", "Bánh xích & Trục vít", "Cơ cấu 4 khâu"],
  focusEn: ["Infrared sensor", "Touch sensor", "If / If-Else logic", "Wait Until", "Counters & Variables", "Bevel gears", "Worm gear & Chains", "Four-bar linkage"],
  link: "/RoboticsCourseHP2Page",
  icon: Eye
}, {
  code: "HP3",
  titleVi: "Xe robot đầu tiên",
  titleEn: "First Mobile Rover",
  ageVi: "Từ 8 tuổi",
  ageEn: "Ages 8+",
  prereqVi: "Hoàn thành HP2",
  prereqEn: "Prerequisite: HP2 completed",
  conceptVi: "Làm chủ chuyển động của robot di động.",
  conceptEn: "Mastering motion and kinematics of mobile rovers.",
  focusVi: ["Khung gầm chuẩn", "Di chuyển chuẩn xác", "Quay 90° / 180° / 360°", "Chu vi bánh xe & Quãng đường", "Hiệu chỉnh sai số", "Lập kế hoạch lộ trình", "Hàm tự tạo My Blocks"],
  focusEn: ["Standard chassis", "Precise movement", "Turning 90°/180°/360°", "Wheel circumference & Distance", "Error correction", "Route planning", "My Blocks subroutines"],
  link: "/RoboticsCourseHP3Page",
  icon: Navigation
}, {
  code: "HP4",
  titleVi: "Xe robot thông minh",
  titleEn: "Intelligent Autonomous Rover",
  ageVi: "Từ 8–9 tuổi",
  ageEn: "Ages 8–9",
  prereqVi: "Hoàn thành HP3",
  prereqEn: "Prerequisite: HP3 completed",
  conceptVi: "Robot biết phản ứng trước môi trường.",
  conceptEn: "Robots reacting autonomously to obstacles and boundaries.",
  focusVi: ["Cảm biến chạm & Hồng ngoại", "Xử lý va chạm", "Tránh chướng ngại vật", "Dừng tại vạch kẻ", "Chống rơi vực", "Đếm vạch", "Cơ cấu gạt bị động", "Timer & Ngẫu nhiên"],
  focusEn: ["Touch & IR dual sensors", "Collision recovery", "Obstacle avoidance", "Line stopping", "Anti-fall cliff safety", "Line counting", "Passive pushing mechanisms", "Timer & Randomization"],
  link: "/RoboticsCourseHP4Page",
  icon: Bot
}, {
  code: "HP5",
  titleVi: "Thành phố xe tự lái",
  titleEn: "Autonomous Smart City",
  ageVi: "Từ 9+ tuổi",
  ageEn: "Ages 9+",
  prereqVi: "Hoàn thành HP4",
  prereqEn: "Prerequisite: HP4 completed",
  conceptVi: "Robot tự hành và lập kế hoạch đường đi.",
  conceptEn: "Autonomous navigation and shortest route pathfinding.",
  focusVi: ["Cảm biến quang độ 5 kênh", "Dò line chính xác", "Giá trị ngưỡng quang học", "Đếm & Xử lý ngã tư", "Bản đồ lộ trình", "Độ lệch & Hiệu chỉnh P trực quan", "Thuật toán đường đi ngắn nhất"],
  focusEn: ["5-channel grayscale sensor", "High-precision line following", "Optical thresholds", "Intersection detection & counting", "Map routing", "Deviation & Intuitive P correction", "Shortest path algorithms"],
  link: "/RoboticsCourseHP5Page",
  icon: Compass
}, {
  code: "HP6",
  titleVi: "Robot nhiệm vụ",
  titleEn: "Mission Strategy & Tournament",
  ageVi: "Từ 9–11 tuổi",
  ageEn: "Ages 9–11",
  prereqVi: "Hoàn thành HP5",
  prereqEn: "Prerequisite: HP5 completed",
  conceptVi: "Thiết kế chiến lược và giải quyết nhiệm vụ như một kỹ sư Robotics.",
  conceptEn: "Architect strategies and solve missions like professional robotics engineers.",
  focusVi: ["Cơ cấu nhiệm vụ chuyên sâu", "Động cơ + Cơ cấu thụ động", "Cơ cấu đẩy, nâng, thả, bắn", "Căn chỉnh vạch chuẩn", "Hàm nhiệm vụ & Biến trạng thái", "Chiến lược thi đấu 150 giây", "Độ ổn định & Nhiệm vụ bí mật", "Mô phỏng giải đấu"],
  focusEn: ["Complex mission mechanisms", "Active motor + passive links", "Pushing, lifting, releasing, launching", "Precision field alignment", "Mission routines & State variables", "150-second strategic mission run", "Reliability & Secret challenges", "Tournament simulation"],
  link: "/RoboticsCourseHP6Page",
  icon: Trophy
}];
const DIFFICULTY_STEPS = [{
  step: "01",
  titleEn: "MECHANICAL ENGINEERING",
  titleVi: "KỸ THUẬT CƠ KHÍ NỀN TẢNG",
  courses: "D1 – D4",
  descVi: "Lắp ráp, chuyển động, bánh răng, đòn bẩy và tư duy thuật toán cơ bản.",
  descEn: "Physical assemblies, gear trains, levers and unplugged logic foundations."
}, {
  step: "02",
  titleEn: "PROGRAMMING",
  titleVi: "LẬP TRÌNH THỰC THỤ",
  courses: "HP1",
  descVi: "Khởi động vi điều khiển, động cơ, kiểm soát góc quay và Block Studio.",
  descEn: "Microcontroller controls, motor speeds, rotation angles and Block Studio."
}, {
  step: "03",
  titleEn: "SENSORS",
  titleVi: "CẢM BIẾN TƯƠNG TÁC",
  courses: "HP2",
  descVi: "Thu nhận dữ liệu từ cảm biến chạm, hồng ngoại, biến số và điều kiện if-else.",
  descEn: "Real-time touch, infrared inputs, dynamic variables and if-else logic."
}, {
  step: "04",
  titleEn: "MOBILE ROBOTICS",
  titleVi: "ROBOT DI ĐỘNG",
  courses: "HP3 – HP4",
  descVi: "Khung gầm chuẩn, điều hướng chính xác, chu vi bánh xe và tránh vật cản.",
  descEn: "Standard chassis, kinematic navigation, wheel circumference and collision avoidance."
}, {
  step: "05",
  titleEn: "AUTONOMOUS ROBOTICS",
  titleVi: "ROBOT TỰ HÀNH",
  courses: "HP5",
  descVi: "Cảm biến quang 5 kênh, bám vạch tinh chỉnh sai số P và bản đồ giao lộ.",
  descEn: "5-channel grayscale array, line tracking with intuitive P calibration, smart routing."
}, {
  step: "06",
  titleEn: "MISSION & STRATEGY",
  titleVi: "CHIẾN THUẬT & NHIỆM VỤ",
  courses: "HP6",
  descVi: "Cơ cấu công tác đa chức năng, tối ưu chuỗi nhiệm vụ 150s và chuẩn bị thi đấu.",
  descEn: "Multi-functional payloads, 150-second mission choreography and tournament readiness."
}];
export default function WhalesBotProgrammingTrack() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  return <section id="whalesbotprogrammingtrack" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden isolate">
      {/* Background Decorative Tech Haillines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-30 [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Header & Language Switcher with WhalesBot Kit Photo */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pb-12 border-b border-border">
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wide">
                <Cpu className="size-3.5" />
                <span>{lang === "vi" ? "GIAI ĐOẠN 2 — ROBOTICS 7+ TUỔI" : "STAGE 02 — ROBOTICS 7+ YEARS"}</span>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <div className="inline-flex items-center bg-card border border-border rounded-lg p-1 shadow-2xs">
                  <Languages className="size-4 ml-2 mr-1 text-muted-foreground" />
                  <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => setLang("vi")} className="text-xs h-7 px-2.5 font-medium">
                    Tiếng Việt
                  </Button>
                  <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => setLang("en")} className="text-xs h-7 px-2.5 font-medium">
                    English
                  </Button>
                </div>
              </div>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
              {lang === "vi" ? "Lập Trình Robot Cùng WhalesBot AI Module 1S" : "WhalesBot AI Module 1S Programming Track"}
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              {lang === "vi" ? "Học sinh chính thức chuyển từ mô hình lắp ráp cơ học sang robot lập trình có bộ điều khiển thông minh, cảm biến đa kênh và giải thuật tự hành. Mỗi khóa học được thiết kế chuẩn mực 12 buổi × 90 phút với tỷ lệ kèm sát 2–3 học sinh/bộ kit." : "Students smoothly transition from structural mechanics to programmable robots with smart microcontrollers, multi-channel sensors, and autonomous algorithms. Every course is crafted for 12 sessions × 90 minutes with 2–3 students per equipment kit."}
            </p>
          </div>

          <div className="lg:col-span-5 relative group">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S Robotics Kit" loading="lazy" className="w-full h-64 sm:h-72 object-cover object-center group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                <span className="text-xs font-mono font-semibold text-white/90 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-md border border-white/20">
                  WhalesBot AI Module 1S Kit • Ages 7+
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Course Standards Meta Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8 p-4 rounded-xl bg-card border border-border shadow-xs">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Cpu className="size-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                {lang === "vi" ? "Nền tảng trang thiết bị" : "Hardware Ecosystem"}
              </p>
              <p className="text-sm font-semibold text-foreground">
                WhalesBot AI Module 1S
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Clock className="size-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                {lang === "vi" ? "Thời lượng đào tạo" : "Course Duration"}
              </p>
              <p className="text-sm font-semibold text-foreground">
                {lang === "vi" ? "12 buổi × 90 phút" : "12 sessions × 90 mins"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-accent/15 text-accent-foreground flex items-center justify-center shrink-0">
              <Users className="size-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                {lang === "vi" ? "Mật độ tương tác" : "Cohort Density"}
              </p>
              <p className="text-sm font-semibold text-foreground">
                {lang === "vi" ? "2–3 học sinh / bộ kit" : "2–3 students / kit"}
              </p>
            </div>
          </div>
        </div>

        {/* 6 Reusable Course Cards: Desktop 3-4 / Tablet 2 / Mobile 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 pt-4">
          {COURSES.map((course, idx) => {
          const Icon = course.icon;
          return <Card key={course.code} data-index={idx} className="relative flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 rounded-2xl overflow-hidden group">
                {/* Header Tag */}
                <div className="h-1.5 w-full bg-primary/20 group-hover:bg-primary transition-colors" />

                <CardHeader className="space-y-3 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="text-xs font-bold px-2.5 py-0.5 border-primary/30 text-primary bg-primary/5">
                      {course.code}
                    </Badge>
                    <Badge variant="secondary" className="text-[11px] font-medium tracking-tight">
                      WHALESBOT AI MODULE 1S
                    </Badge>
                  </div>

                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {lang === "vi" ? course.titleVi : course.titleEn}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium text-muted-foreground mt-1 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 font-semibold text-foreground/80">
                        <Activity className="size-3 text-secondary" />
                        {lang === "vi" ? course.ageVi : course.ageEn}
                      </span>
                      <span>•</span>
                      <span className="text-muted-foreground">
                        {lang === "vi" ? course.prereqVi : course.prereqEn}
                      </span>
                    </CardDescription>
                  </div>

                  {/* Concept Quote */}
                  <div className="rounded-lg bg-muted/60 p-3 border-l-2 border-primary text-xs italic text-foreground/90 font-medium leading-relaxed">
                    "{lang === "vi" ? course.conceptVi : course.conceptEn}"
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-1">
                  <div>
                    <p className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-2">
                      {lang === "vi" ? "Trọng tâm kiến thức kỹ thuật" : "Core Technical Competencies"}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(lang === "vi" ? course.focusVi : course.focusEn).map((f, i) => <span key={i} className="inline-block px-2 py-0.5 rounded-md text-[11px] font-medium bg-secondary/15 text-secondary-foreground border border-secondary/20">
                          {f}
                        </span>)}
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="pt-3 border-t border-border mt-auto">
                  <Button asChild className="w-full gap-2 justify-center shadow-xs">
                    <Link to={course.link}>
                      <span>{lang === "vi" ? `XEM CHI TIẾT ${course.code}` : `EXPLORE ${course.code}`}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        {/* Section 9: Technical Difficulty Progression Graphic */}
        <div className="mt-20 pt-16 border-t border-border">
          <div className="text-center max-w-2xl mx-auto space-y-3 mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              <Layers className="size-3.5" />
              <span>{lang === "vi" ? "LỘ TRÌNH ĐỘ KHÓ KỸ THUẬT" : "PROGRESSION OF TECHNICAL DIFFICULTY"}</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              {lang === "vi" ? "TỪ LẮP RÁP ĐẾN ROBOT TỰ HÀNH" : "FROM MECHANICAL ASSEMBLY TO AUTONOMOUS MISSIONS"}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              {lang === "vi" ? "Học sinh xây dựng nền tảng tư duy từng bước logic chặt chẽ, không đốt cháy giai đoạn để nắm vững kỹ năng kỹ sư." : "A systematic, step-by-step path designed to cultivate deep engineering thinking without skipping fundamental layers."}
            </p>
          </div>

          {/* Stepped Progression: Horizontal on desktop, Stepped timeline on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 relative">
            {DIFFICULTY_STEPS.map((stepItem, idx) => <div key={stepItem.step} data-index={idx} className="relative flex flex-col p-5 rounded-xl border border-border bg-card shadow-2xs hover:border-primary/50 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono font-bold text-primary px-2 py-0.5 rounded bg-primary/10">
                    {stepItem.step}
                  </span>
                  <span className="text-[11px] font-semibold text-secondary-foreground bg-secondary/30 px-2 py-0.5 rounded">
                    {stepItem.courses}
                  </span>
                </div>

                <h4 className="text-sm font-bold text-foreground tracking-tight mb-2 uppercase">
                  {lang === "vi" ? stepItem.titleVi : stepItem.titleEn}
                </h4>

                <p className="text-xs text-muted-foreground leading-relaxed mt-auto">
                  {lang === "vi" ? stepItem.descVi : stepItem.descEn}
                </p>

                {/* Connecting Arrow for Desktop (1-5) */}
                {idx < DIFFICULTY_STEPS.length - 1 && <div aria-hidden="true" className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 size-6 rounded-full bg-card border border-border items-center justify-center text-muted-foreground shadow-2xs">
                    <ArrowRight className="size-3" />
                  </div>}
              </div>)}
          </div>

          {/* Bottom Roadmap Summary Action */}
          <div className="mt-12 p-6 rounded-2xl bg-secondary/15 border border-secondary/30 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <Sparkles className="size-4 text-primary" />
                <h4 className="font-semibold text-foreground text-base">
                  {lang === "vi" ? "Sẵn sàng định hướng lộ trình học cho con?" : "Ready to plan your child's robotics pathway?"}
                </h4>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {lang === "vi" ? "Đội ngũ chuyên môn ROBOTO hỗ trợ kiểm tra trình độ đầu vào miễn phí cho học sinh từ 7 tuổi." : "ROBOTO academic advisors offer free assessment test sessions for students aged 7 and above."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default" className="shadow-xs">
                <Link to="/FreeTrialContactPage">
                  {lang === "vi" ? "ĐĂNG KÝ TƯ VẤN & TEST ĐẦU VÀO" : "BOOK ADMISSION ASSESSMENT"}
                </Link>
              </Button>
              <Button asChild variant="outline" size="default">
                <Link to="/LearningPathCurriculumPage">
                  {lang === "vi" ? "XEM TOÀN BỘ LỘ TRÌNH" : "VIEW FULL ROADMAP"}
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}